import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { LAHDE } from '@/constants/testIds';

function fmt(n) {
  return new Intl.NumberFormat('fi-FI').format(n || 0);
}

export default function LahdePanel({ date, externalMeta }) {
  const [meta, setMeta] = useState(null);
  const [varastossa, setVarastossa] = useState('');
  const [noukittu, setNoukittu] = useState('');
  const [focused, setFocused] = useState(null);

  const load = useCallback(async () => {
    if (!date) return;
    try {
      const { data } = await api.get(`/daily-meta/${date}`);
      setMeta(data);
      if (focused !== 'varastossa') setVarastossa(String(data.varastossa_kg || 0));
      if (focused !== 'noukittu') setNoukittu(String(data.noukittu_kg || 0));
    } catch {}
  }, [date, focused]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!externalMeta || externalMeta.date !== date) return;
    const m = externalMeta.meta || externalMeta;
    setMeta(m);
    if (focused !== 'varastossa') setVarastossa(String(m.varastossa_kg || 0));
    if (focused !== 'noukittu') setNoukittu(String(m.noukittu_kg || 0));
  }, [externalMeta, date, focused]);

  const commit = async (field, value) => {
    setFocused(null);
    const num = value === '' ? 0 : Number(String(value).replace(',', '.'));
    if (Number.isNaN(num)) return;
    if (meta && (meta[`${field}_kg`] || 0) === num) return;
    try {
      const { data } = await api.patch(`/daily-meta/${date}`, { [`${field}_kg`]: num });
      setMeta(data);
    } catch {}
  };

  const saldo = meta?.saldo_kg ?? 0;
  const saldoNegative = saldo < 0;

  return (
    <div className="bg-white border border-[#E5E6E3] rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 flex items-center gap-4 text-sm flex-wrap">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#6A6D66] shrink-0">Päivän tase</span>

        <Cell label="Varastossa">
          <input
            data-testid={LAHDE.varastossa}
            value={varastossa}
            onChange={(e) => setVarastossa(e.target.value)}
            onFocus={() => setFocused('varastossa')}
            onBlur={() => commit('varastossa', varastossa)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            inputMode="decimal"
            className="font-mono w-16 text-right bg-transparent border border-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] focus:outline-none rounded px-1 py-0.5 text-sm transition-all"
          />
        </Cell>

        <Cell label="Noukittu">
          <input
            data-testid={LAHDE.noukittu}
            value={noukittu}
            onChange={(e) => setNoukittu(e.target.value)}
            onFocus={() => setFocused('noukittu')}
            onBlur={() => commit('noukittu', noukittu)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            inputMode="decimal"
            className="font-mono w-16 text-right bg-transparent border border-transparent hover:border-[#E5E6E3] focus:border-[#2A4B30] focus:ring-1 focus:ring-[#2A4B30] focus:outline-none rounded px-1 py-0.5 text-sm transition-all"
          />
        </Cell>

        <Cell label="Tilaukset">
          <span data-testid={LAHDE.tilaukset} className="font-mono text-sm text-[#1C1C1A]">{fmt(meta?.tilaukset_kg)}</span>
          {(meta?.arviot_kg ?? 0) > 0 && (
            <span
              data-testid="lahde-arviot"
              title="Tilauksista arviomäärät (status = Arvio)"
              className="font-mono text-[11px] text-[#6A6D66] ml-1"
            >
              (arviot {fmt(meta.arviot_kg)})
            </span>
          )}
        </Cell>

        <Cell label="Saldo" bold>
          <span
            data-testid={LAHDE.saldo}
            className={`font-mono text-sm font-bold px-1.5 py-0.5 rounded ${saldoNegative ? 'bg-[#FBEAE5] text-[#C05A3C]' : 'bg-[#E8F0E9] text-[#3B6E44]'}`}
          >
            {fmt(saldo)} kg
          </span>
        </Cell>
      </div>
    </div>
  );
}

function Cell({ label, children, bold }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-[#6A6D66]">{label}</span>
      {children}
    </div>
  );
}
